import { NavLink, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, BookOpen, Layers, Users, CalendarSearch, BarChart3, LogOut, ChevronRight, ChevronDown, GraduationCap, Lightbulb, AlertTriangle, Eye, Calendar, ClipboardList, ClipboardCheck, RefreshCw, Ticket, UserCheck, PieChart, ShieldAlert, FileText, Bookmark, Filter, Receipt, Scale, Trophy, Gavel, Sparkles, Settings, Home, Activity, Briefcase, MapPin, DoorOpen, Award } from 'lucide-react';
import { Presentation, Target } from 'lucide-react';import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import NotificationBell from './NotificationBell';
import AskBaskyBubble from './askBasky/AskBaskyBubble';

/**
 * Sidebar is grouped into sections for clarity:
 *  OVERVIEW  → Dashboard
 *  ACADEMICS → Masters, Batches, Students, Course Syllabus, Slot Finder
 *  TRAINERS  → everything trainer / attendance related
 *  GOVERNANCE → Audit Bookmarks, Anomalies, Tickets, Ticket Performance, Minutes of Meeting
 *  FINANCE   → Credit Notes, Reconciliation, Document Library
 *  REPORTS   → All Reports, Overdue, Partially Paid, Attendance Report, Custom Views
 *  SYSTEM    → Sync Settings, Feature Requests
 */
export const navSections = [
  {
    key: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    items: [
      { path: '/training/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    key: 'academics',
    label: 'Academics',
    icon: BookOpen,
    items: [
      { path: '/training/masters', label: 'Masters', icon: BookOpen },
      { path: '/training/batches', label: 'Batch Management', icon: Layers },
      { path: '/training/students', label: 'Students', icon: Users },
      { path: '/training/placement', label: 'Placement Module', icon: Briefcase },
      { path: '/venue', label: 'Venue Requirements', icon: MapPin, external: true },
      { path: '/training/course-syllabus', label: 'Course Syllabus', icon: BookOpen },
      { path: '/training/slot-finder', label: 'Slot Finder', icon: CalendarSearch },
    ],
  },
  {
    key: 'trainers',
    label: 'Trainers',
    icon: GraduationCap,
    items: [
      {
        label: 'Trainer', icon: GraduationCap, groupKey: 'trainer',
        children: [
          { path: '/training/trainer-overview', label: 'Trainer Overview', icon: Eye },
          { path: '/training/trainer-overview-v2', label: 'Trainer Overview V2', icon: Award },
          { path: '/training/trainer-scorecard', label: 'Trainer Scorecard', icon: Trophy },
          { path: '/training/new-scorecard', label: 'New Scorecard', icon: Sparkles },
          { path: '/training/trainer-dashboard', label: 'Trainer Dashboard', icon: PieChart },
          { path: '/training/trainer-lab', label: 'Trainer Lab', icon: DoorOpen },
          { path: '/training/daily-attendance', label: 'Daily Attendance', icon: Calendar },
          { path: '/training/dpr-report', label: 'DPR Report', icon: ClipboardList },
          { path: '/training/pdp-mock-status', label: 'PDP / Mock Status', icon: ClipboardList },
        ],
      },
      {
        label: 'Trainer Demo', icon: Presentation, groupKey: 'trainer-demo',
        children: [
          { path: '/training/trainer-demo', label: 'Demo Queue', icon: Presentation, end: true },
          { path: '/training/trainer-demo/new', label: 'Add New Demo', icon: CalendarSearch },
          { path: '/training/trainer-demo/analytics', label: 'Demo Analytics', icon: BarChart3 },
          { path: '/training/trainer-demo/incentive', label: 'Incentive Statement', icon: Trophy, adminManagerOnly: true },
          { path: '/training/trainer-demo/setup', label: 'Setup', icon: Settings, adminOnly: true },
        ],
      },
      { path: '/training/trainer-performance', label: 'Trainer Review', icon: Award, managerialOnly: true },
      { path: '/training/trainer-target', label: 'Trainer Target', icon: Target, managerialOnly: true },
      { path: '/training/attendance-report', label: 'Attendance Report', icon: UserCheck },
    ],
  },
  {
    key: 'vouchers',
    label: 'Vouchers',
    icon: Ticket,
    items: [
      { path: '/training/vouchers', label: 'Exam Vouchers', icon: Ticket, managerialOnly: true },
    ],
  },
  {
    key: 'trainer-audit',
    label: 'Trainer Audit',
    icon: ClipboardCheck,
    items: [
      { path: '/training/trainer-audit/form', label: 'Audit Form', icon: ClipboardCheck },
      { path: '/training/trainer-audit/report', label: 'Audit Report', icon: BarChart3 },
      { path: '/training/dpr-compliance', label: 'DPR Compliance', icon: ClipboardList },
      { path: '/training/trainer-audit/assignments', label: 'Manager Assignments', icon: Users, adminManagerOnly: true },
    ],
  },
  {
    key: 'governance',
    label: 'Governance',
    icon: ShieldAlert,
    items: [
      { path: '/training/audit-bookmarks', label: 'Audit Bookmarks', icon: Bookmark },
      { path: '/training/anomalies', label: 'Anomalies', icon: AlertTriangle },
      { path: '/training/tickets', label: 'Tickets', icon: Ticket },
      { path: '/training/tickets/performance', label: 'Ticket Performance', icon: Trophy },
      { path: '/training/mom', label: 'Minutes of Meeting', icon: Gavel },
      { path: '/training/activity', label: 'Activity Timeline', icon: Activity },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: Receipt,
    items: [
      { path: '/training/credit-notes', label: 'Credit Notes', icon: Receipt },
      { path: '/training/cn-review', label: 'Credit Note Review', icon: ShieldAlert, adminManagerOnly: true },
      { path: '/training/reconciliation', label: 'Reconciliation', icon: Scale },
      { path: '/training/document-library', label: 'Document Library', icon: FileText },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: BarChart3,
    items: [
      {
        label: 'Training Reports', icon: BarChart3, groupKey: 'reports',
        children: [
          { path: '/training/reports', label: 'All Reports', icon: BarChart3 },
          { path: '/training/overdue-report', label: 'Overdue Report', icon: ShieldAlert },
          { path: '/training/partially-paid-report', label: 'Partially Paid', icon: FileText },
        ],
      },
      { path: '/training/custom-views-prototype', label: 'Custom Views', icon: Filter },
    ],
  },
  {
    key: 'system',
    label: 'System',
    icon: Settings,
    items: [
      { path: '/training/whats-new', label: "What's New", icon: Sparkles, badgeKey: 'whatsNew' },
      { path: '/training/sync-settings', label: 'Sync Settings', icon: RefreshCw },
      { path: '/training/feature-requests', label: 'Feature Requests', icon: Lightbulb },
      { path: '/training/menu-settings', label: 'Menu Settings', icon: Settings, adminOnly: true },
    ],
  },
];

// Flatten for convenience
const trainingNav = navSections.flatMap(s => s.items);

/** Detect which section contains the given pathname. */
function sectionOfPath(pathname) {
  for (const section of navSections) {
    for (const item of section.items) {
      if (item.groupKey) {
        if (item.children.some(c => pathname.startsWith(c.path))) return section.key;
      } else if (pathname.startsWith(item.path)) {
        return section.key;
      }
    }
  }
  return navSections[0].key;
}

const roleColors = {
  admin: 'bg-red-100 text-red-700',
  manager: 'bg-blue-100 text-blue-700',
  restricted_user: 'bg-purple-100 text-purple-700',
};

function isGroupActive(children, pathname) {
  return children.some(c => pathname.startsWith(c.path));
}

function NavGroup({ item, pathname, openGroups, toggleGroup }) {
  const isActive = isGroupActive(item.children, pathname);
  const isOpen = openGroups[item.groupKey];

  return (
    <div>
      <button
        onClick={() => toggleGroup(item.groupKey)}
        data-testid={`training-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        className={`flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors duration-150 w-full text-left ${
          isActive
            ? 'bg-violet-100 text-violet-800'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1">{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-slate-100 pl-2">
          {item.children.map(child => (
            <NavLink
              key={child.path}
              to={child.path}
              end={child.end}
              data-testid={`training-nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive: active }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-sm text-xs font-medium transition-colors duration-150 ${
                  active
                    ? 'bg-violet-600 text-white'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`
              }
            >
              <child.icon className="w-3.5 h-3.5 flex-shrink-0" />
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrainingLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeSection, setActiveSection] = useState(() => sectionOfPath(location.pathname));
  const [whatsNewUnread, setWhatsNewUnread] = useState(0);

  const refreshWhatsNewCount = useCallback(async () => {
    try {
      const res = await api.get('/whats-new/unread-count');
      setWhatsNewUnread(res.data.unread || 0);
    } catch {}
  }, []);

  useEffect(() => {
    refreshWhatsNewCount();
    const onRead = () => refreshWhatsNewCount();
    window.addEventListener('whats-new-read', onRead);
    return () => window.removeEventListener('whats-new-read', onRead);
  }, [refreshWhatsNewCount]);

  // Auto-switch section when navigating via link (e.g., from a card on dashboard)
  useEffect(() => {
    setActiveSection(sectionOfPath(location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    trainingNav.filter(i => i.groupKey).forEach(g => {
      initial[g.groupKey] = isGroupActive(g.children, location.pathname);
    });
    return initial;
  });

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [hiddenMap, setHiddenMap] = useState({});
  useEffect(() => {
    const load = () => api.get('/nav-visibility').then(r => setHiddenMap(r.data.hidden || {})).catch(() => {});
    load();
    window.addEventListener('nav-visibility-changed', load);
    return () => window.removeEventListener('nav-visibility-changed', load);
  }, []);

  const currentSection = navSections.find(s => s.key === activeSection) || navSections[0];

  // Training-manager (audit-only) users see just the Trainer Audit section.
  const isAuditOnly = user?.role === 'training_manager';
  const isCounsellor = user?.role === 'counsellor';
  const roleAllows = (i) => !(hiddenMap[i.path] || []).includes(user?.role) && !(i.adminOnly && user?.role !== 'admin') &&
    !(i.adminManagerOnly && !['admin', 'manager'].includes(user?.role)) &&
    !(i.managerialOnly && !['admin', 'manager', 'training_manager'].includes(user?.role));
  const baseSections = isAuditOnly
    ? navSections.filter(s => s.key === 'trainer-audit')
    : isCounsellor
      ? navSections.filter(s => s.key === 'trainers').map(s => ({
          ...s, label: 'Trainer Demo', icon: Presentation,
          items: s.items.filter(i => i.groupKey === 'trainer-demo'),
        }))
      : navSections;
  const visibleSections = baseSections
    .map(s => ({
      ...s,
      items: s.items.filter(roleAllows).map(i => (
        i.groupKey ? { ...i, children: i.children.filter(roleAllows) } : i
      )).filter(i => !i.groupKey || i.children.length),
    }));
  const activeVisible = visibleSections.find(s => s.key === activeSection) || visibleSections[0];

  // Audit-only managers are confined to the Trainer Audit module.
  if (user?.role === 'training_manager' && !location.pathname.startsWith('/training/trainer-audit')) {
    return <Navigate to="/training/trainer-audit/form" replace />;
  }
  // Counsellors are confined to the Trainer Demo module.
  if (isCounsellor && !location.pathname.startsWith('/training/trainer-demo')) {
    return <Navigate to="/training/trainer-demo" replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden" data-testid="training-layout">
      {/* Icon rail */}
      <aside className="w-14 flex-shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col items-center py-3" data-testid="training-rail">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-sm mb-3" title="Training Management">
          <GraduationCap className="w-5 h-5 text-white" />
        </div>

        <div className="flex-1 flex flex-col items-center gap-1 w-full">
          {visibleSections.map(section => {
            const Icon = section.icon;
            const isActive = activeSection === section.key;
            return (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                title={section.label}
                data-testid={`rail-${section.key}`}
                className={`group relative w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-150 ${
                  isActive
                    ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-200'
                    : 'text-slate-500 hover:bg-white hover:text-slate-800'
                }`}
              >
                {isActive && (
                  <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-violet-600" />
                )}
                <Icon className="w-[18px] h-[18px]" />
                {section.key === 'system' && whatsNewUnread > 0 && (
                  <span data-testid="rail-system-dot" className="absolute top-1 right-1 w-2 h-2 rounded-full bg-violet-500 ring-2 ring-slate-50" />
                )}
                <span className="pointer-events-none absolute left-full ml-3 px-2 py-1 rounded bg-slate-900 text-white text-[11px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50">
                  {section.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Bottom: Command Center + user */}
        <div className="flex flex-col items-center gap-1.5 pt-3 border-t border-slate-200 w-full">
          {user?.role !== 'restricted_user' && user?.role !== 'training_manager' && (
            <NavLink
              to="/dashboard"
              data-testid="back-to-command-center"
              title="Command Center"
              className="group relative w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 hover:bg-white hover:text-violet-700 transition-colors"
            >
              <Home className="w-[18px] h-[18px]" />
              <span className="pointer-events-none absolute left-full ml-3 px-2 py-1 rounded bg-slate-900 text-white text-[11px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50">
                Command Center
              </span>
            </NavLink>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-9 h-9 rounded-lg overflow-hidden hover:ring-2 hover:ring-violet-300 transition-all" data-testid="training-user-menu">
                <Avatar className="w-9 h-9 rounded-lg">
                  <AvatarFallback className="rounded-lg bg-violet-600 text-white text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" className="w-56">
              <DropdownMenuItem className="flex-col items-start gap-0.5" disabled>
                <span className="text-sm font-semibold">{user?.full_name}</span>
                <span className="text-[10px] text-slate-500">{user?.email}</span>
                <Badge variant="secondary" className={`mt-1 text-[10px] px-1.5 py-0 ${roleColors[user?.role] || 'bg-gray-100 text-gray-600'}`}>
                  {user?.role?.replace('_', ' ')}
                </Badge>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="training-logout-btn" onClick={handleLogout} className="text-red-600">
                <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Context panel */}
      <aside className="w-56 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col" data-testid="training-sidebar">
        <div className="h-16 flex items-center px-5 border-b border-slate-200">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Section</p>
            <h2 className="text-sm font-bold text-slate-800 tracking-tight">{activeVisible.label}</h2>
          </div>
        </div>
        <ScrollArea className="flex-1 py-3">
          <nav className="px-3 space-y-0.5">
            {activeVisible.items.map(item => {
              if (item.groupKey) {
                return (
                  <NavGroup
                    key={item.groupKey}
                    item={item}
                    pathname={location.pathname}
                    openGroups={openGroups}
                    toggleGroup={toggleGroup}
                  />
                );
              }
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`training-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors duration-150 ${
                      isActive
                        ? 'bg-violet-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {item.badgeKey === 'whatsNew' && whatsNewUnread > 0 && (
                    <span data-testid="whats-new-badge" className="ml-auto bg-violet-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight">
                      {whatsNewUnread > 99 ? '99+' : whatsNewUnread}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 glass-header border-b border-border flex items-center justify-between px-8 sticky top-0 z-40" data-testid="training-header">
          <div />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <span className="text-xs font-data text-muted-foreground">{new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </header>
        <ScrollArea className="flex-1">
          <main className="p-6 md:p-8 lg:p-10 max-w-[1600px] w-full min-w-0">
            <Outlet />
          </main>
        </ScrollArea>
      </div>
      <AskBaskyBubble />
    </div>
  );
}
